import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { staffApi, ApiError } from "@/api";
import type { QuoteTemplate } from "@/types";
import { Button, Spinner, TextField, TextAreaField } from "@/components/ui";
import "./templates.css";

/**
 * Combines the template picker (201 list + 199 create-from-template) and
 * template management (200 create + 202 retire/restore). The table is seeded
 * empty per the spec, so the create form is always available.
 */
export function TemplatesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [templates, setTemplates] = useState<QuoteTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [usingId, setUsingId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showRetired, setShowRetired] = useState(false);

  async function reload() {
    setLoading(true);
    setError(undefined);
    try {
      setTemplates(await staffApi.listTemplates());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load templates.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  // The header's "New template" routes here with { create: true } — open the
  // inline create form and strip the flag so refresh/back doesn't re-open it.
  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      setShowCreate(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  async function useTemplate(t: QuoteTemplate) {
    setUsingId(t.id);
    try {
      const quote = await staffApi.createFromTemplate(t.id, {});
      navigate(`/staff/quotes/${quote.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create from template.");
      setUsingId(null);
    }
  }

  async function toggleActive(t: QuoteTemplate) {
    try {
      await staffApi.updateTemplate(t.id, { ...t, is_active: !t.is_active });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update the template.");
    }
  }

  /** Set (or clear, with 0) the default template; server enforces at-most-one
   *  and returns the full refreshed list. */
  async function setDefault(templateId: number) {
    try {
      const list = await staffApi.setDefaultTemplate(templateId);
      setTemplates(list);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not set the default.");
    }
  }

  const hasDefault = templates.some((t) => t.is_default);
  const retiredCount = templates.filter((t) => !t.is_active).length;
  // Retired templates are hidden by default; the toggle reveals them.
  const visible = showRetired ? templates : templates.filter((t) => t.is_active);

  return (
    <section className="templates">
      <header className="templates__head">
        <div>
          <h1>Templates</h1>
          <p className="templates__count">
            {visible.length} template(s){retiredCount > 0 ? ` · ${retiredCount} retired` : ""}
          </p>
        </div>
        <div className="templates__head-actions">
          {retiredCount > 0 ? (
            <label className="templates__toggle">
              <input
                type="checkbox"
                checked={showRetired}
                onChange={(e) => setShowRetired(e.target.checked)}
              />
              Show retired
            </label>
          ) : null}
          {hasDefault ? (
            <Button variant="ghost" onClick={() => setDefault(0)}>Clear default</Button>
          ) : null}
        </div>
      </header>

      {error ? <p className="quotes-list__error" role="alert">{error}</p> : null}

      {showCreate ? (
        <CreateTemplateForm
          onCreated={async () => {
            setShowCreate(false);
            await reload();
          }}
          onCancel={() => setShowCreate(false)}
          onError={setError}
        />
      ) : null}

      {loading ? (
        <div className="templates__loading"><Spinner /></div>
      ) : visible.length === 0 ? (
        <p className="templates__empty">
          {templates.length === 0
            ? "No templates yet. Create one above to enable the “new from template” flow."
            : "All templates are retired. Toggle “Show retired” to see them."}
        </p>
      ) : (
        <ul className="templates__list">
          {visible.map((t) => (
            <li key={t.id} className={`templates__card${!t.is_active && editingId !== t.id ? " templates__card--retired" : ""}`}>
              {editingId === t.id ? (
                <EditTemplateDetailsForm
                  template={t}
                  onSaved={async () => {
                    setEditingId(null);
                    await reload();
                  }}
                  onCancel={() => setEditingId(null)}
                  onError={setError}
                />
              ) : (
                <>
                  <div className="templates__card-main">
                    <h2>
                      {t.name}
                      {t.is_default ? <span className="templates__default-tag">Default</span> : null}
                      {!t.is_active ? <span className="templates__retired-tag">Retired</span> : null}
                    </h2>
                    {t.description ? <p className="templates__desc">{t.description}</p> : null}
                    <p className="templates__meta">
                      {(t.default_line_items?.length ?? 0)} default line item(s) ·{" "}
                      {(t.content?.blocks?.length ?? 0)} block(s)
                    </p>
                    <label className="templates__default-toggle">
                      <input
                        type="radio"
                        name="default-template"
                        checked={!!t.is_default}
                        disabled={!t.is_active}
                        onChange={() => setDefault(t.id)}
                      />
                      Default for new quotes
                    </label>
                  </div>
                  <div className="templates__card-actions">
                    <Button
                      variant="primary"
                      onClick={() => useTemplate(t)}
                      loading={usingId === t.id}
                      disabled={!t.is_active}
                    >
                      Use template
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setEditingId(t.id)}>
                      Edit details
                    </Button>
                    <Link to={`/staff/templates/${t.id}/edit`} className="templates__edit-link">
                      <Button variant="secondary" size="sm">Edit blocks</Button>
                    </Link>
                    <Button variant="ghost" size="sm" onClick={() => toggleActive(t)}>
                      {t.is_active ? "Retire" : "Restore"}
                    </Button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CreateTemplateForm({
  onCreated,
  onCancel,
  onError,
}: {
  onCreated: () => void | Promise<void>;
  onCancel: () => void;
  onError: (msg: string) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name.trim()) {
      onError("Give the template a name.");
      return;
    }
    setBusy(true);
    try {
      // Seed a minimal valid block document; staff refine it on the quote.
      await staffApi.createTemplate({
        name: name.trim(),
        description: description.trim() || undefined,
        content: {
          version: 1,
          blocks: [
            { id: "b1", type: "cover", props: { title: name.trim() } },
            { id: "b2", type: "pricing", props: { heading: "Fees" } },
            { id: "b3", type: "accept", props: { require_name: true } },
          ],
        },
        default_line_items: [],
        is_active: true,
      });
      setName("");
      setDescription("");
      await onCreated();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not create the template.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="templates__create">
      <h2>New template</h2>
      <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
      <TextAreaField
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <div className="templates__create-actions">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="secondary" onClick={submit} loading={busy}>Create template</Button>
      </div>
    </div>
  );
}

/**
 * Inline editor for a template's title and description only — the blocks live
 * behind "Edit blocks" (TemplateBuilder). PATCH 202 is a full-object update, so
 * the rest of the template is spread through unchanged.
 */
function EditTemplateDetailsForm({
  template,
  onSaved,
  onCancel,
  onError,
}: {
  template: QuoteTemplate;
  onSaved: () => void | Promise<void>;
  onCancel: () => void;
  onError: (msg: string) => void;
}) {
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description ?? "");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name.trim()) {
      onError("Give the template a name.");
      return;
    }
    setBusy(true);
    try {
      await staffApi.updateTemplate(template.id, {
        ...template,
        name: name.trim(),
        description: description.trim() || undefined,
      });
      await onSaved();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not update the template.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="templates__card-edit">
      <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
      <TextAreaField
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <div className="templates__create-actions">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="secondary" size="sm" onClick={submit} loading={busy}>Save details</Button>
      </div>
    </div>
  );
}
